import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  OutlinedInput,
  Stack,
  Card,
  CircularProgress,
  LinearProgress,
  Switch,
  Tooltip
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { API_BASES } from '../../config/constants';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import DataTable from '../../components/DataTable';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import dayjs from 'dayjs';

import { useMasterVehicleList } from '../../stores/masterVehicleStore';

import { fetcherDataReportTrip } from '../../stores/reportStore';
import { fetchRetase, fetchRetaseDetail, fetchVisitationRetase } from '../../stores/retaseStore';
import { timezone } from '../../utils/timeZone';
import { useCompanyDataList } from '../../stores/companyStore';
import PickerField from '../../components/PickerField';
import MasterVehicleAddEdit from '../master-data/vehicle/MasterVehicleAddEdit';
import MasterCompanyAddEdit from '../master-data/company/MasterCompanyAddEdit';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useLanguage } from '../../utils/i18n';
import { useRouteTripAll } from '../../stores/routeTripStore';
import InformationBubbleDialog from '../../components/InformationBubbleDialog';
import { Tabs, Tab } from '@mui/material';
import RitaseSummaryTable from './RitaseSummaryTable';

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

import { useNotifier } from '../../components/Notifier';
import RitaseDetailReport from './RitaseDetailReport';
import RitaseDetailReportSort from './RitaseDetailReportSort';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const selectFieldStyle = {
  minWidth: 150,
  height: 41,
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '& fieldset': {
      borderColor: '#485D9A'
    },
    '&:hover fieldset': {
      borderColor: '#485D9A'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#485D9A'
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#485D9A'
  }
};

const formatSecondsToHMS = (totalSeconds) => {
  if (totalSeconds === undefined || totalSeconds === null || totalSeconds === '-' || isNaN(totalSeconds)) return '-';
  const sec = parseInt(totalSeconds, 10);
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

function DataReportsPageContent() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const reportType = queryParams.get('name');
  const navigate = useNavigate();
  const notify = useNotifier();
  const { t } = useLanguage();

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedPools, setSelectedPools] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [rawRitaseData, setRawRitaseData] = useState([]);
  const [sortByRoute, setSortByRoute] = useState(false);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [activeTab, setActiveTab] = useState('All');
  const [autoStartSearch, setAutoStartSearch] = useState(false);

  useEffect(() => {
    const state = location.state;
    if (state && state.autoSearch === true && state.fromDate && state.toDate && state.vehicleId) {
      setFromDate(state.fromDate);
      setToDate(state.toDate);
      setPeriodType('custom');

      handleVehicleChange([state.vehicleId]);
      setAutoStartSearch(true);

      // Clear the state so it doesn't trigger again on refresh
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, location.search]);

  useEffect(() => {
    if (autoStartSearch && selectedVehicles.length > 0 && fromDate && toDate) {
      handleGenerateReport();
      setAutoStartSearch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartSearch, selectedVehicles, fromDate, toDate]);

  useEffect(() => {
    setRows([]);
    setRawRitaseData([]);
    setPaginationModel({ page: 0, pageSize: 10 });
  }, [reportType]);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const companyIdParam = selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all') ? '' : selectedCompanyIds.join(',');
  const { data: allRoutes = [] } = useRouteTripAll(0, 10000, '', '', companyIdParam);

  const columnsFleets = useMemo(
    () => [
      { field: 'licensePlate', headerName: t('license.plateNumber', 'Plate Number'), width: 120 },
      {
        field: 'startTime',
        headerName: t('achievement.startTime', 'Start Time'),
        width: 170,
        valueGetter: (value, row) => (value && value !== '-' ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-')
      },
      {
        field: 'endTime',
        headerName: t('achievement.endTime', 'End Time'),
        width: 170,
        valueGetter: (value, row) => (value && value !== '-' ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-')
      },
      { field: 'duration', headerName: t('achievement.duration', 'Duration'), width: 120 },
      { field: 'total_distance', headerName: t('reportModule.distanceKm', 'Distance (km)'), width: 150 },
      {
        field: 'avg_speed',
        headerName: t('exportWaypoint.speed', 'Speed'),
        width: 160,
        renderCell: (params) => (
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
            <Typography variant="body2" fontWeight="500">
              {params.row.avg_speed !== '-' ? `${params.row.avg_speed} km/h` : '-'}
            </Typography>
            {params.row.min_speed !== '-' && params.row.max_speed !== '-' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5, lineHeight: 1.2 }}>
                {t('reportModule.min', 'Min.')} {params.row.min_speed} km/h <br /> {t('reportModule.max', 'Max.')} {params.row.max_speed}{' '}
                km/h
              </Typography>
            )}
          </Box>
        )
      },
      {
        field: 'rpm',
        headerName: t('exportWaypoint.rpm', 'RPM'),
        width: 160,
        renderCell: (params) => (
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
            <Typography variant="body2" fontWeight="500">
              {params.row.rpm !== '-' ? params.row.rpm : '-'}
            </Typography>
            {params.row.min_rpm !== '-' && params.row.max_rpm !== '-' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5, lineHeight: 1.2 }}>
                {t('reportModule.min', 'Min.')} {params.row.min_rpm}
                <br /> {t('reportModule.max', 'Max.')} {params.row.max_rpm}
              </Typography>
            )}
          </Box>
        )
      },
      { field: 'location', headerName: t('notification.location', 'Location'), flex: 1, minWidth: 250 },
      {
        field: 'coordinate',
        headerName: t('exportWaypoint.coordinate', 'Coordinate'),
        width: 200,
        renderCell: (params) => {
          if (!params.row.lat || !params.row.lon || params.row.lat === '-' || params.row.lon === '-') return '-';
          return (
            <a
              href={`http://maps.google.com/maps?z=18&q=${params.row.lat},${params.row.lon}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'none' }}
            >
              {params.row.lat}, {params.row.lon}
            </a>
          );
        }
      }
    ],
    [t]
  );

  const columnsTrips = useMemo(
    () => [
      { field: 'dateFormatted', headerName: t('general.date', 'Date'), width: 120 },
      { field: 'licensePlate', headerName: t('license.plateNumber', 'Plate Number'), width: 130 },
      { field: 'distance', headerName: t('vehicleFuelSummary.distance', 'Disatance (km)'), width: 150 },
      {
        field: 'duration',
        headerName: t('achievement.duration', 'Duration'),
        width: 120,
        valueGetter: (value, row) => formatSecondsToHMS(value)
      },
      {
        field: 'startTime',
        headerName: t('achievement.startTime', 'Start Time'),
        width: 170,
        valueGetter: (value, row) => (value && value !== '-' ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-')
      },
      {
        field: 'endTime',
        headerName: t('achievement.endTime', 'End Time'),
        width: 170,
        valueGetter: (value, row) => (value && value !== '-' ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-')
      },
      { field: 'startOdometer', headerName: t('reports.startOdometer', 'Start Odometer'), width: 150 },
      { field: 'endOdometer', headerName: t('reports.endOdometer', 'End Odometer'), width: 150 },
      {
        field: 'enginOn',
        headerName: t('reports.engineOn', 'EngineOn'),
        width: 120,
        valueGetter: (value, row) => formatSecondsToHMS(value)
      },
      {
        field: 'engineOff',
        headerName: t('reports.engineOff', 'Engine Off'),
        width: 120,
        valueGetter: (value, row) => formatSecondsToHMS(value)
      },
      {
        field: 'idle_time',
        headerName: t('reports.engineIdle', 'Idle Time'),
        width: 120,
        valueGetter: (value, row) => formatSecondsToHMS(value)
      }
    ],
    [t]
  );

  const columnsDrivers = useMemo(
    () => [
      { field: 'licensePlate', headerName: t('license.plateNumber', 'Plate Number'), width: 150 },
      { field: 'driverName', headerName: t('dashboardAsset.driverName', 'Driver Name'), width: 150 },
      { field: 'distance', headerName: t('exportWaypoint.distance', 'Jarak Tempuh'), width: 150 },
      { field: 'startLocation', headerName: t('reports.startLocation', 'Start Location'), width: 250 },
      { field: 'endLocation', headerName: t('reports.endLocation', 'End Location'), flex: 1, minWidth: 250 },
      { field: 'averageSpeed', headerName: t('reports.averageSpeed', 'Average Speed'), width: 120 },
      { field: 'maxSpeed', headerName: t('vehicleType.maxSpeed', 'Max Speed'), width: 120 },
      { field: 'minSpeed', headerName: t('vehicleType.minSpeed', 'Min Speed'), width: 120 }
    ],
    [t]
  );

  const generateDynamicColumns = () => {
    const start = dayjs(fromDate);
    const end = dayjs(toDate);
    const diffDays = end.diff(start, 'day') + 1;
    const cols = [{ field: 'licensePlate', headerName: t('achievement.vehicleNumber', 'Vehicle Number'), width: 180 }];
    for (let i = 0; i < diffDays; i++) {
      const currentDate = start.add(i, 'day');
      const dateStr = currentDate.format('YYYY-MM-DD');
      cols.push({
        field: dateStr,
        headerName: currentDate.format('DD/MM'),
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const val = params.value || 0;
          if (val > 0) {
            return (
              <Tooltip title={t('reportModule.showDetail', 'Show detail')}>
                <Box
                  sx={{
                    cursor: 'pointer',
                    color: '#1976d2',
                    // textDecoration: 'underline',
                    fontWeight: 'bold'
                  }}
                  onClick={() => {
                    const startOfDay = currentDate.startOf('day').format('YYYY-MM-DD HH:mm:ss');
                    const endOfDay = currentDate.endOf('day').format('YYYY-MM-DD HH:mm:ss');
                    const vehicleId = params.row.id?.replace('summary-', '') || '';
                    navigate(`/data-reports?name=ritase_details`, {
                      state: {
                        autoSearch: true,
                        fromDate: startOfDay,
                        toDate: endOfDay,
                        vehicleId: vehicleId
                      }
                    });
                  }}
                >
                  {val}
                </Box>
              </Tooltip>
            );
          }
          return val;
        }
      });
    }
    return cols;
  };

  const activeConfig = useMemo(() => {
    switch (reportType) {
      case 'vehicle_report':
        return { title: t('reports.vehicleReport', 'Report Vehicle'), columns: columnsFleets };
      case 'route_report':
      case 'trip_report':
        return { title: t('reports.tripReport', 'Report Trip'), columns: columnsTrips };
      case 'driver_report':
        return { title: t('reports.driverReport', 'Report Driver'), columns: columnsDrivers };
      case 'ritase_summary':
        return { title: t('reports.ritaseSummaryReport', 'Ritase Summary Report'), columns: generateDynamicColumns() };
      case 'ritase_details':
        return { title: t('reports.ritaseDetailReport', 'Ritase Detail Report'), columns: [] };
      default:
        return { title: t('reportModule.dataReport', 'Data Report'), columns: [] };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, fromDate, toDate, t, columnsFleets, columnsTrips, columnsDrivers]);

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c.value ?? c),
        label: c.original?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
        companyName:
          c.original?.join?.company_name ||
          c.original?.company_name ||
          c.join?.company_name ||
          c.company_name ||
          c.companyName ||
          t('reportModule.unknownCompany', 'Unknown Company'),
        raw: c.raw ?? c
      })),
    [vehicleList, t]
  );

  const selectedVehicleIds = useMemo(() => selectedVehicles.map((v) => String(v.value)), [selectedVehicles]);
  const selectedPoolIds = useMemo(() => selectedPools.map((v) => String(v.value)), [selectedPools]);

  const handleVehicleChange = (ids) => {
    const newSelected = ids.map((id) => {
      const strId = String(id);
      const match = vehicleOptionsNormalized.find((v) => String(v.value) === strId);
      if (match) return match;
      const prevMatch = selectedVehicles.find((v) => String(v.value) === strId);
      if (prevMatch) return prevMatch;

      const rawMatch =
        (vehicleList || []).find((v) => String(v.value) === strId) ||
        (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
      if (rawMatch) {
        return {
          value: strId,
          label: rawMatch.raw?.license_plate ?? rawMatch.original?.license_plate ?? rawMatch.label ?? strId,
          companyName: rawMatch.companyName,
          raw: rawMatch.raw
        };
      }
      return { value: strId, label: strId };
    });
    setSelectedVehicles(newSelected);
  };

  const fetchAddressAll = async (lat, lon) => {
    if (!lat || !lon || lat == 0 || lon == 0) return '-';
    try {
      const url = `${API_BASES.ADDRESS}/reverse.php?lat=${lat}&lon=${lon}&zoom=18&format=json&addressdetails=1&limit=1`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      return data.data.display_name || `${lat}, ${lon}`;
    } catch (error) {
      console.warn(`Failed geocode ${lat},${lon}:`, error);
      return `${lat}, ${lon}`;
    }
  };

  // const formatDuration = (seconds) => {
  //   if (!seconds) return '0m';
  //   const hours = Math.floor(seconds / 3600);
  //   const minutes = Math.floor((seconds % 3600) / 60);
  //   if (hours > 0) {
  //     return `${hours} Jam ${minutes} Menit`;
  //   }
  //   return `${minutes} Menit`;
  // };

  const handleGenerateReport = async () => {
    if (selectedVehicleIds.length === 0) {
      notify({ message: t('reports.errSelectVehicle', 'Select at least one vehicle'), severity: 'warning' });
      return;
    }
    if (!fromDate || !toDate) {
      notify({ message: t('reports.errSelectDatePeriod', 'Please select a date period'), severity: 'warning' });
      return;
    }
    if (dayjs(fromDate).isAfter(dayjs(toDate))) {
      notify({ message: t('reports.errFromToDate', 'The From date cannot be greater than the To date.'), severity: 'error' });
      return;
    }

    setLoading(true);
    setLoadingProgress(0);
    setLoadingText(t('reportModule.checkingData', 'Checking data...'));
    setRows([]);
    setRawRitaseData([]);

    try {
      let completedVehicles = 0;
      const totalVehicles = selectedVehicleIds.length;
      setLoadingText(
        `${t('reportModule.preparingProcessFor', 'Preparing the process for')} ${totalVehicles} ${t('reportModule.vehiclesEllipsis', 'vehicles...')}`
      );

      if (reportType === 'ritase_details' || reportType === 'ritase_summary' || reportType === 'trip_report') {
        const allRows = [];
        const chunkSize = 5;
        for (let i = 0; i < totalVehicles; i += chunkSize) {
          const chunk = selectedVehicleIds.slice(i, i + chunkSize);
          const chunkResults = await Promise.all(
            chunk.map(async (vId) => {
              const vehicleInfo =
                selectedVehicles.find((v) => String(v.value) === String(vId)) ||
                vehicleOptionsNormalized.find((v) => String(v.value) === String(vId));
              const licensePlate = vehicleInfo?.label || String(vId);
              if (!vId || vId === 'all') return [];

              try {
                const companyId = vehicleInfo?.raw?.company || vehicleInfo?.original?.company || localStorage.getItem('companyId') || '';

                let apiChunks = [];
                const localStart = dayjs(fromDate);
                const localEnd = dayjs(toDate);
                const pType = periodType;
                const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

                if (isFullDayPeriod) {
                  let curr = localStart.startOf('day');
                  const end = localEnd.startOf('day');
                  while (curr.isBefore(end) || curr.isSame(end, 'day')) {
                    apiChunks.push({
                      startLocal: curr.startOf('day'),
                      endLocal: curr.endOf('day'),
                      dateObj: curr,
                      dateStr: curr.format('YYYY-MM-DD')
                    });
                    curr = curr.add(1, 'day');
                  }
                } else {
                  let curr = localStart.startOf('day');
                  const end = localEnd.startOf('day');
                  while (curr.isBefore(end) || curr.isSame(end, 'day')) {
                    let chunkStart = curr.startOf('day');
                    let chunkEnd = curr.endOf('day');
                    if (curr.isSame(localStart, 'day')) chunkStart = localStart;
                    if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

                    apiChunks.push({
                      startLocal: chunkStart,
                      endLocal: chunkEnd,
                      dateObj: curr,
                      dateStr: curr.format('YYYY-MM-DD')
                    });
                    curr = curr.add(1, 'day');
                  }
                }

                const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
                const dailyPromises = apiChunks.map(async (chunk) => {
                  const dateObj = chunk.dateObj;
                  const dateStr = chunk.dateStr;
                  try {
                    const queryParams = {
                      vehicleId: String(vId),
                      companyId: String(companyId),
                      tz,
                      startTime: chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
                      endTime: chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]')
                    };

                    const resDetail = await fetchRetase(queryParams);
                    const apiDataRaw = sortByRoute ? (resDetail?.data?.retase ?? []) : (resDetail?.data?.route_sort ?? []);

                    // Deduplicate trips with the same Route Title, Start Time, and End Time
                    const uniqueTripsMap = new Map();
                    apiDataRaw.forEach((tripData) => {
                      const routes = tripData.routes || [];
                      const details = tripData.details || [];

                      const spatialNames = routes.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
                      const routeTitle =
                        spatialNames.length > 0
                          ? spatialNames.join(' - ')
                          : `${t('reportModule.routeIdLabel', 'Route ID:')} ${tripData.route_trip_id}`;

                      let earliestStart = null;
                      let latestEnd = null;
                      details.forEach((d) => {
                        const st = dayjs(d.start);
                        const en = dayjs(d.end);
                        if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
                        if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
                      });

                      const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
                      const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';

                      const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
                      if (!uniqueTripsMap.has(uniqueKey)) {
                        uniqueTripsMap.set(uniqueKey, tripData);
                      }
                    });
                    const apiData = Array.from(uniqueTripsMap.values());

                    const allRitaseTimeline = [];
                    apiData.forEach((tData, tIdx) => {
                      const rNames = tData.routes?.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
                      const rTitle =
                        rNames?.length > 0 ? rNames.join(' - ') : `${t('reportModule.routeIdLabel', 'Route ID:')} ${tData.route_trip_id}`;
                      (tData.details || []).forEach((d, idx) => {
                        allRitaseTimeline.push({
                          tripData: tData,
                          tIdx,
                          d,
                          dIdx: idx,
                          start: dayjs(d.start),
                          end: dayjs(d.end),
                          routeTitle: rTitle,
                          ritaseNum: idx + 1
                        });
                      });
                    });
                    allRitaseTimeline.sort((a, b) => a.start.valueOf() - b.start.valueOf());

                    const tripsWithDetailsNested = await Promise.all(
                      apiData.map(async (tripData, tripIdx) => {
                        const totalTrips = tripData.total_trips || 1;
                        const routes = tripData.routes || [];
                        const details = tripData.details || [];

                        if (details.length === 0) {
                          const rawTripObj = {
                            vehicle_id: vId,
                            license_plate: vehicleInfo?.label || t('dashboardAsset.unknown', 'Unknown'),
                            route_trip_id: tripData.route_trip_id,
                            driver_name: tripData.driver_name,
                            in_time: 'no-start',
                            out_time: 'no-end',
                            duration: 0,
                            total_trips: totalTrips
                          };
                          return [
                            {
                              id: `${vId}-${dateStr}-${tripIdx}-${tripData.route_trip_id}-empty`,
                              dateFormatted: dateObj.format('DD/MM/YYYY'),
                              licensePlate: vehicleInfo?.label,
                              distance: `0.00 Km`,
                              duration: 0,
                              startTime: '-',
                              endTime: '-',
                              startOdometer: '0.00',
                              endOdometer: '0.00',
                              enginOn: 0,
                              engineOff: 0,
                              idle_time: 0,
                              driver_name: tripData.driver_name,
                              rawTrip: rawTripObj,
                              visits: routes.map((rt) => ({ ...rt })),
                              deepDetails: [],
                              nextRitaseLabel: null
                            }
                          ];
                        }

                        const detailPromises = details.map(async (d, dIdx) => {
                          const st = dayjs(d.start);
                          const en = dayjs(d.end);

                          const finalStartIso = st.toISOString().replace(/\.\d{3}Z$/, 'Z');
                          const finalEndIso = en.toISOString().replace(/\.\d{3}Z$/, 'Z');

                          let nextRouteTimeIso = undefined;

                          const currentTimelineIdx = allRitaseTimeline.findIndex((rt) => rt.tIdx === tripIdx && rt.dIdx === dIdx);

                          if (currentTimelineIdx !== -1 && currentTimelineIdx < allRitaseTimeline.length - 1) {
                            const nextSt = allRitaseTimeline[currentTimelineIdx + 1].start;
                            nextRouteTimeIso = nextSt.toISOString().replace(/\.\d{3}Z$/, 'Z');
                          }

                          const finalStart = st.format('YYYY-MM-DD HH:mm:ss');
                          const finalEnd = en.format('YYYY-MM-DD HH:mm:ss');
                          const totalDur = d.dur || 0;

                          let deepDetailLogs = [];
                          if (reportType === 'ritase_details' || (reportType === 'trip_report' && finalStart && finalEnd)) {
                            try {
                              const detailRes = await fetchRetaseDetail({
                                companyId: String(companyId),
                                vehicleId: String(vId),
                                routeTripId: String(tripData.route_trip_id),
                                startTime: finalStartIso,
                                endTime: finalEndIso,
                                nextRouteTime: nextRouteTimeIso,
                                tz,
                                page: 0,
                                rowsPerPage: 10000
                              });
                              deepDetailLogs = detailRes?.data?.data || detailRes?.data || [];
                            } catch (detailErr) {
                              console.error(`Gagal ambil detail kedalaman untuk trip ${tripData.route_trip_id}`, detailErr);
                            }
                          }

                          let totalDistance = 0;
                          let totalEngineOn = 0;
                          let totalEngineOff = 0;
                          let totalIdle = 0;
                          let startOdo = 0;
                          let endOdo = 0;
                          let actualStartTime = '-';
                          let actualEndTime = '-';

                          if (deepDetailLogs && deepDetailLogs.length > 0) {
                            const firstLog = deepDetailLogs[0];
                            const lastLog = deepDetailLogs[deepDetailLogs.length - 1];

                            actualStartTime = firstLog?.start_time ? dayjs(firstLog.start_time) : '-';
                            actualEndTime = lastLog?.end_time ? dayjs(lastLog.end_time) : '-';
                            startOdo = firstLog?.start_odo != null ? firstLog.start_odo / 1000 : 0;
                            endOdo = lastLog?.end_odo != null ? lastLog.end_odo / 1000 : 0;

                            await Promise.all(
                              deepDetailLogs.map(async (log) => {
                                if (log?.trip_info?.start_time && log?.trip_info?.end_time) {
                                  try {
                                    const visitData = await fetchVisitationRetase({
                                      companyId: String(companyId),
                                      vehicleId: String(vId),
                                      startTime: log.trip_info.start_time,
                                      endTime: log.trip_info.end_time,
                                      tz
                                    });
                                    log.trip_info.visitation = visitData?.data || visitData || [];
                                  } catch (err) {
                                    console.error('Error fetching visitation for trip_info', err);
                                    log.trip_info.visitation = [];
                                  }
                                }
                              })
                            );

                            deepDetailLogs.forEach((log) => {
                              totalDistance += Number(log.end_odo || 0) - Number(log.start_odo || 0);

                              totalEngineOn += Number(log.engine_on_duration || 0);
                              totalEngineOff += Number(log.engine_off_duration || 0);
                              totalIdle += Number(log.engine_idle_duration || 0);
                            });
                          }

                          const actualLicensePlate = tripData.license_plate || licensePlate;

                          const rawTripObj = {
                            vehicle_id: tripData.vehicle_id || vId,
                            license_plate: actualLicensePlate,
                            route_trip_id: tripData.route_trip_id,
                            driver_name: tripData.driver_name,
                            in_time: finalStart,
                            out_time: finalEnd,
                            duration: totalDur,
                            total_trips: totalTrips
                          };
                          const totalDuratinAllEngine = totalEngineOn + totalEngineOff + totalIdle;

                          return {
                            id: `${tripData.vehicle_id || vId}-${dateStr}-${tripIdx}-${tripData.route_trip_id}-${dIdx}`,
                            dateFormatted: dateObj.format('DD/MM/YYYY'),
                            licensePlate: actualLicensePlate,

                            distance: `${(totalDistance / 1000).toFixed(2)} Km`,
                            duration: totalDuratinAllEngine,
                            startTime: actualStartTime,
                            endTime: actualEndTime,
                            startOdometer: Number(startOdo).toFixed(2),
                            endOdometer: Number(endOdo).toFixed(2),
                            enginOn: totalEngineOn,
                            engineOff: totalEngineOff,
                            idle_time: totalIdle,

                            driver_name: tripData.driver_name,
                            rawTrip: rawTripObj,
                            visits: routes.map((rt) => ({ ...rt })),
                            deepDetails: deepDetailLogs
                          };
                        });

                        return await Promise.all(detailPromises);
                      })
                    );

                    const tripsWithDetails = tripsWithDetailsNested.flat();

                    return tripsWithDetails;
                  } catch (err) {
                    console.error(`Gagal fetch trip kendaraan ${vId} pada tanggal ${dateStr}`, err);
                    return [];
                  }
                });
                const dailyResults = await Promise.all(dailyPromises);
                return dailyResults.flat();
              } finally {
                completedVehicles++;
                setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
                setLoadingText(
                  `${t('reportModule.processingUnit', 'Processing unit:')} ${licensePlate} (${completedVehicles}/${totalVehicles})`
                );
              }
            })
          );
          allRows.push(...chunkResults.flat());
        }

        if (reportType === 'ritase_details' || reportType === 'ritase_summary' || reportType === 'trip_report') {
          const rawData = allRows.map((r) => {
            const matchedRoute = allRoutes.find((route) => String(route.id) === String(r.rawTrip.route_trip_id));
            const isActiveRoute = matchedRoute ? matchedRoute.is_active === true || matchedRoute.is_active === 'true' : false;

            return {
              ...r.rawTrip,
              isActiveRoute,
              visits: r.visits,
              deepDetails: r.deepDetails
            };
          });
          setRawRitaseData(rawData);

          if (reportType === 'ritase_summary') {
            const start = dayjs(fromDate);
            const end = dayjs(toDate);
            const diffDays = end.diff(start, 'day') + 1;
            const dateHeaders = [];
            for (let i = 0; i < diffDays; i++) {
              dateHeaders.push(start.add(i, 'day').format('YYYY-MM-DD'));
            }

            const groupedByVehicle = {};
            rawData.forEach((item) => {
              const poolVisit = item.visits?.find((v) => v.spatial_type === 'pool' || v.spatial_type === 'geofence');
              const itemPoolId = poolVisit ? String(poolVisit.spatial_id) : null;
              if (selectedPoolIds.length > 0 && itemPoolId && !selectedPoolIds.includes(itemPoolId)) {
                return;
              }

              const vId = item.vehicle_id;
              if (!groupedByVehicle[vId]) {
                groupedByVehicle[vId] = {
                  id: `summary-${vId}`,
                  no: Object.keys(groupedByVehicle).length + 1,
                  licensePlate: item.license_plate,
                  totalVehicleRitase: 0
                };
                dateHeaders.forEach((date) => {
                  groupedByVehicle[vId][date] = 0;
                });
              }

              if (item.in_time) {
                const itemDate = dayjs(item.in_time).format('YYYY-MM-DD');
                if (groupedByVehicle[vId][itemDate] !== undefined) {
                  groupedByVehicle[vId][itemDate] += 1;
                  groupedByVehicle[vId].totalVehicleRitase += 1;
                }
              }
            });

            setRows(Object.values(groupedByVehicle));
          } else {
            setRows(allRows);
          }
        }
        setLoading(false);
        return;
      }
      const allRowsOther = [];
      const chunkSize = 5;
      for (let i = 0; i < totalVehicles; i += chunkSize) {
        const chunk = selectedVehicleIds.slice(i, i + chunkSize);
        const chunkResultsOther = await Promise.all(
          chunk.map(async (vId) => {
            const vehicleInfo =
              selectedVehicles.find((v) => String(v.value) === String(vId)) ||
              vehicleOptionsNormalized.find((v) => String(v.value) === String(vId));
            const licensePlate = vehicleInfo?.label || String(vId);
            if (!vId || vId === 'all') return [];

            try {
              const vehicleCompanyId = vehicleInfo?.raw?.company || vehicleInfo?.original?.company || null;

              if (reportType === 'vehicle_report' || reportType === 'driver_report') {
                let apiChunks = [];
                const localStart = dayjs(fromDate);
                const localEnd = dayjs(toDate);
                const pType = periodType;
                const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

                if (isFullDayPeriod) {
                  let curr = localStart.startOf('day');
                  const end = localEnd.startOf('day');
                  while (curr.isBefore(end) || curr.isSame(end, 'day')) {
                    apiChunks.push({
                      startLocal: curr.startOf('day'),
                      endLocal: curr.endOf('day'),
                      dateObj: curr,
                      dateStr: curr.format('YYYY-MM-DD')
                    });
                    curr = curr.add(1, 'day');
                  }
                } else {
                  let curr = localStart.startOf('day');
                  const end = localEnd.startOf('day');
                  while (curr.isBefore(end) || curr.isSame(end, 'day')) {
                    let chunkStart = curr.startOf('day');
                    let chunkEnd = curr.endOf('day');
                    if (curr.isSame(localStart, 'day')) chunkStart = localStart;
                    if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

                    apiChunks.push({
                      startLocal: chunkStart,
                      endLocal: chunkEnd,
                      dateObj: curr,
                      dateStr: curr.format('YYYY-MM-DD')
                    });
                    curr = curr.add(1, 'day');
                  }
                }

                const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
                const dailyPromises = apiChunks.map(async (chunk) => {
                  const finalStartIso = chunk.startLocal.format('YYYY-MM-DD HH:mm:ss');
                  const finalEndIso = chunk.endLocal.format('YYYY-MM-DD HH:mm:ss');
                  const dateObj = chunk.dateObj;
                  const res = await fetcherDataReportTrip(vId, finalStartIso, finalEndIso, vehicleCompanyId, tz);

                  let list = [];
                  if (Array.isArray(res?.data)) {
                    list = res.data;
                  } else if (Array.isArray(res?.data?.data)) {
                    list = res.data.data;
                  } else if (Array.isArray(res)) {
                    list = res;
                  } else if (res && typeof res === 'object' && Object.keys(res).length > 0) {
                    list = [res];
                  }

                  if (reportType === 'vehicle_report') {
                    return list.map((item, idx) => {
                      const lat = item.end_latitude != null && item.end_latitude !== 0 ? item.end_latitude : item.start_latitude || '-';
                      const lon = item.end_longitude != null && item.end_longitude !== 0 ? item.end_longitude : item.start_longitude || '-';
                      return {
                        id: `${vId}-vehicle-${dateObj.format('YYYYMMDD')}-${idx}`,
                        dateFormatted: dateObj.format('DD/MM/YYYY'),
                        licensePlate: item.vehicle?.license_plate || vehicleInfo?.label || '-',
                        startTime: item.start_time || '-',
                        endTime: item.end_time || '-',
                        duration: item.duration || '-',
                        total_distance: item.total_distance != null ? Number(item.total_distance).toFixed(2) : '-',
                        max_speed: item.max_speed != null ? Number(item.max_speed).toFixed(2) : '-',
                        min_speed: item.min_speed != null ? Number(item.min_speed).toFixed(2) : '-',
                        avg_speed: item.avg_speed != null ? Number(item.avg_speed).toFixed(2) : '-',
                        max_rpm: item.max_rpm != null ? Number(item.max_rpm).toFixed(2) : '-',
                        min_rpm: item.min_rpm != null ? Number(item.min_rpm).toFixed(2) : '-',
                        rpm: item.avg_rpm != null ? Number(item.avg_rpm).toFixed(2) : '-',
                        location: item.end_location || '-',
                        lat,
                        lon,
                        coordinate: lat !== '-' && lon !== '-' ? `${lat}, ${lon}` : '-'
                      };
                    });
                  } else {
                    return await Promise.all(
                      list.map(async (item, idx) => {
                        let startAddress = item.start_location;
                        let endAddress = item.end_location;

                        if (!startAddress) {
                          startAddress = await fetchAddressAll(item.start_latitude, item.start_longitude);
                        }
                        if (!endAddress) {
                          endAddress = await fetchAddressAll(item.end_latitude, item.end_longitude);
                        }

                        return {
                          id: `${vId}-driver-${dateObj.format('YYYYMMDD')}-${idx}`,
                          licensePlate: item.vehicle?.license_plate || vehicleInfo?.label || '-',
                          driverName: item.driver_name || '-',
                          distance: item.total_distance ? `${item.total_distance.toFixed(2)} Km` : t('reportModule.0Km', '0 Km'),
                          startLocation: startAddress,
                          endLocation: endAddress,
                          averageSpeed: item.avg_speed ? `${Number(item.avg_speed).toFixed(1)} km/h` : t('reportModule.0KmH', '0 km/h'),
                          maxSpeed: item.max_speed != null ? `${item.max_speed} km/h` : t('reportModule.0KmH', '0 km/h'),
                          minSpeed: item.min_speed != null ? `${item.min_speed} km/h` : t('reportModule.0KmH', '0 km/h')
                        };
                      })
                    );
                  }
                });

                const dailyResults = await Promise.all(dailyPromises);
                return dailyResults.flat();
              }

              return [];
            } finally {
              completedVehicles++;
              setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
              setLoadingText(
                `${t('reportModule.processingUnit', 'Processing unit:')} ${licensePlate} (${completedVehicles}/${totalVehicles})`
              );
            }
          })
        );
        allRowsOther.push(...chunkResultsOther.flat());
      }

      const resultsOther = allRowsOther;
      setRows(resultsOther.flat());
    } catch (error) {
      console.error('Error generating report:', error);
      notify({ message: t('reports.errFetchData', 'Failed to retrieve report data. Check the console.'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!tableSearchQuery) return rows;
    const lowerQuery = tableSearchQuery.toLowerCase();

    return rows.filter((row) => {
      return (
        (row.licensePlate && row.licensePlate.toLowerCase().includes(lowerQuery)) ||
        (row.driverName && row.driverName.toLowerCase().includes(lowerQuery)) ||
        (row.locationName && row.locationName.toLowerCase().includes(lowerQuery))
      );
    });
  }, [rows, tableSearchQuery]);

  const summaryTotals = useMemo(() => {
    if (reportType !== 'ritase_summary' || rows.length === 0) {
      return { totalVehicles: 0, totalRitase: 0, averagePerShift: 0 };
    }

    const totalVehicles = rows.length;
    let totalRitaseAll = 0;

    rows.forEach((row) => {
      totalRitaseAll += row.totalVehicleRitase || 0;
    });

    const start = dayjs(fromDate);
    const end = dayjs(toDate);
    const diffDays = end.diff(start, 'day') + 1 || 1;

    const averagePerShift = totalVehicles > 0 ? totalRitaseAll / (totalVehicles * diffDays) : 0;

    return {
      totalVehicles: totalVehicles,
      totalRitase: totalRitaseAll,
      averagePerShift: averagePerShift.toFixed(1)
    };
  }, [rows, reportType, fromDate, toDate]);

  const [dateError, setDateError] = useState('');

  const validateDateRange = (startDayjs, endDayjs) => {
    if (!startDayjs || !endDayjs || !startDayjs.isValid() || !endDayjs.isValid()) {
      setDateError(t('reportModule.errTimeEmptyOrInvalid', 'Start and end time cannot be empty or invalid'));
      return false;
    }
    const diffDays = endDayjs.diff(startDayjs, 'day', true);
    const diffMinutes = endDayjs.diff(startDayjs, 'minute');

    if (diffDays > 31) {
      setDateError(t('reportModule.errMaxRange1Month', 'Maximum timeframe of one month'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('reports.errStartTimeLater', 'Start time cannot be later than end time'));
      return false;
    } else {
      setDateError('');
      return true;
    }
  };

  const [periodType, setPeriodType] = useState('today');

  useEffect(() => {
    if (!periodType) return;
    if (periodType === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
    } else if (periodType === 'week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  const getReportDescription = (type, tFn) => {
    switch (type) {
      case 'vehicle_report':
        return tFn('reports.vehicleDesc', 'Detailed vehicle travel history with vehicle location, travel distance, and trip duration.');
      case 'driver_report':
        return tFn('reports.driverDesc', "Every driver's operational details, including travel distance, total travel and stop duration.");
      case 'trip_report':
        return tFn(
          'reports.tripDesc',
          'Detailed trip report for each vehicle reference to report ritase, including distance, start and end times, and trip duration.'
        );
      case 'ritase_summary':
        return tFn('reports.ritaseSummaryDesc', 'Summary of ritase data for each vehicle, including total ritase and average ritase.');
      case 'ritase_details':
        return tFn('reports.ritaseDetailsDesc', 'Detailed view of each ritase with location, distance, and duration information.');
      default:
        return '';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box mb={1}>
        <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
          <StorageIcon fontSize="large" color="primary" />
          {activeConfig.title}
        </Typography>
      </Box>
      <Box sx={{ mb: 1 }}>
        <Paper sx={{ p: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <Box
            sx={{
              p: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 2
            }}
          >
            {/* <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} /> */}
            <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
              {getReportDescription(reportType, t)}
            </Typography>
            {(localStorage.getItem('rolename') === 'Administrator' ||
              (() => {
                try {
                  return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes(
                    '"modules_path":"report-catalog"'
                  );
                } catch {
                  return false;
                }
              })()) && (
              <Button
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={() => navigate('/report-catalog?name=report-catalog')}
                size="small"
                variant="contained"
                sx={{
                  bgcolor: 'primary',
                  color: '#ffffff',
                  textTransform: 'none',
                  px: 0.5,
                  py: 0,
                  minWidth: 0,
                  '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                }}
              >
                {t('reports.reportCatalog', 'Report Catalog')}
              </Button>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            {/* Filter Company */}
            <Box sx={{ flex: '1 1 200px' }}>
              <PickerField
                label={t('general.company', 'Company')}
                value={selectedCompanyIds}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids || []);
                  setSelectedVehicles([]);
                }}
                allowAdd
                AddComponent={MasterCompanyAddEdit}
                dialogTitle={t('general.selectCompany', 'Select Company')}
                multiSelect={true}
              />
            </Box>

            {/* Filter Kendaraan */}
            <Box sx={{ flex: '1 1 200px' }}>
              <PickerField
                label={t('reports.selectVehicle', 'Select Vehicle')}
                value={selectedVehicleIds}
                selectedItems={selectedVehicles}
                {...vehiclePickerParams}
                options={vehicleOptionsNormalized}
                onChange={handleVehicleChange}
                dialogTitle={t('reports.selectVehicle', 'Select Vehicle')}
                multiSelect={true}
                allowAdd
                AddComponent={MasterVehicleAddEdit}
              />
            </Box>

            {/* Filter Preset Periode */}
            <Box sx={{ flex: '0 1 180px' }}>
              <FormControl fullWidth size="medium" sx={selectFieldStyle}>
                <InputLabel id="period-label">{t('reports.selectPeriod', 'Pilih Periode')}</InputLabel>
                <MuiSelect
                  labelId="period-label"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  disabled={loading}
                  input={<OutlinedInput label={t('reports.selectPeriod', 'Pilih Periode')} />}
                >
                  <MenuItem value="today">{t('mapsTracking.today', 'Today')}</MenuItem>
                  <MenuItem value="week">{t('time.lastWeek', 'Last Week')}</MenuItem>
                  <MenuItem value="month">{t('time.lastMonth', 'Last Month')}</MenuItem>
                  <MenuItem value="custom">{t('reportModule.customMax1Month', 'Custom (Max 1 Month)')}</MenuItem>
                </MuiSelect>
              </FormControl>
            </Box>

            {periodType === 'custom' && (
              <Box sx={{ flex: '1 1 250px' }}>
                <Stack direction="row" spacing={2}>
                  <DateTimePicker
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    label={t('reports.from', 'From')}
                    format="DD/MM/YYYY HH:mm:ss"
                    disabled={periodType !== 'custom' || loading}
                    value={fromDate ? dayjs(fromDate) : null}
                    onChange={(val) => {
                      if (val) {
                        setFromDate(val.format('YYYY-MM-DD HH:mm:00'));
                        validateDateRange(val, dayjs(toDate));
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!dateError,
                        helperText: dateError
                      },
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [
                          { name: 'preventOverflow', options: { boundary: 'window', padding: 8 } },
                          { name: 'flip', options: { fallbackPlacements: ['top-start', 'left-start', 'right-start'] } }
                        ],
                        sx: { zIndex: 100000 }
                      }
                    }}
                  />
                  <DateTimePicker
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    label={t('reports.to', 'To')}
                    format="DD/MM/YYYY HH:mm:ss"
                    disabled={periodType !== 'custom' || loading}
                    value={toDate ? dayjs(toDate) : null}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    maxDate={periodType === 'custom' && fromDate ? dayjs(fromDate).add(1, 'month') : undefined}
                    onChange={(v) => {
                      if (v) {
                        setToDate(v.format('YYYY-MM-DD HH:mm:59'));
                        setPeriodType('custom');
                        validateDateRange(dayjs(fromDate), v);
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!dateError
                      },
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [
                          { name: 'preventOverflow', options: { boundary: 'window', padding: 8 } },
                          { name: 'flip', options: { fallbackPlacements: ['top-start', 'left-start', 'right-start'] } }
                        ],
                        sx: { zIndex: 100000 }
                      }
                    }}
                  />
                </Stack>
              </Box>
            )}

            {/* Aksi Reset & Generate */}
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                variant="outlined"
                color="secondary"
                disabled={loading}
                onClick={() => {
                  setSelectedCompanyIds(() => {
                    const defaultId = getCompanyIdFromStorage();
                    return defaultId ? [defaultId] : [];
                  });
                  setSelectedVehicles([]);
                  setSelectedPools([]);
                  setPeriodType('today');
                  setRows([]);
                  setRawRitaseData([]);
                  setDateError('');
                }}
              >
                {t('general.reset', 'Reset')}
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleGenerateReport}
                disabled={loading}
                sx={{
                  px: 3,
                  fontWeight: 'bold',
                  '&.Mui-disabled': {
                    bgcolor: '#cfcfcf',
                    color: '#8f8f8f'
                  }
                }}
              >
                {loading ? t('general.search', 'Search') : t('general.searching', 'Searching')}
              </Button>
            </Box>
          </Box>
        </Paper>

        {fromDate && toDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, px: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: 'rgba(72, 93, 154, 0.05)',
                border: '1px solid rgba(72, 93, 154, 0.2)',
                boxShadow: '0 2px 8px rgba(72, 93, 154, 0.05)'
              }}
            >
              <InfoOutlinedIcon sx={{ color: '#485D9A' }} />
              <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                {t('revenue.showingDataFrom', 'Showing data from')}{' '}
                <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                  {dayjs(fromDate).format('DD MMMM YYYY HH:mm:ss')}
                </Box>{' '}
                {t('revenue.to', 'to')}{' '}
                <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                  {dayjs(toDate).format('DD MMMM YYYY HH:mm:ss')}
                </Box>
              </Typography>
            </Box>

            {reportType === 'ritase_details' && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    sx={{
                      minHeight: 36,
                      bgcolor: '#f1f5f9',
                      borderRadius: '10px',
                      p: 0.5,
                      '& .MuiTabs-indicator': { display: 'none' },
                      '& .MuiTab-root': {
                        minHeight: 28,
                        py: 0.5,
                        px: 2,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        color: '#64748b',
                        transition: 'all 0.2s',
                        '&.Mui-selected': {
                          bgcolor: '#ffffff',
                          color: '#1e3a8a',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }
                      }
                    }}
                  >
                    <Tab label={t('reports.all', 'All')} value="All" />
                    <Tab label={t('achievement.activeRoute', 'Active Route')} value="Active Route" />
                    <Tab label={t('achievement.historicalRoute', 'Historical Route')} value="Historical Route" />
                  </Tabs>
                  <InformationBubbleDialog
                    iconSize="small"
                    content="Ritase is detected based on currently active routes and non-active routes. Non-active routes are kept for historical purposes because when there is a route change in the past, the route before that time is still saved."
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="600" sx={{ mr: 1, color: '#334155' }}>
                    {t('reportModule.groupByRoute', 'Group by Route')}
                  </Typography>
                  <Switch checked={sortByRoute} onChange={(e) => setSortByRoute(e.target.checked)} color="primary" />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
      {reportType === 'ritase_details' ? (
        (() => {
          const filteredData = rawRitaseData.filter((item) => {
            if (activeTab === 'Active Route') return item.isActiveRoute;
            if (activeTab === 'Historical Route') return !item.isActiveRoute;
            return true;
          });

          return sortByRoute ? (
            <RitaseDetailReport
              data={filteredData}
              loading={loading}
              loadingProgress={loadingProgress}
              loadingText={loadingText}
              fromDate={fromDate}
              toDate={toDate}
            />
          ) : (
            <RitaseDetailReportSort
              data={filteredData}
              loading={loading}
              loadingProgress={loadingProgress}
              loadingText={loadingText}
              fromDate={fromDate}
              toDate={toDate}
            />
          );
        })()
      ) : (
        <>
          {reportType === 'ritase_summary' && (
            <>
              {/* <Paper
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2,
                  mb: 2,
                  bgcolor: '#485D9A',
                  color: 'white',
                  borderRadius: 2
                }}
              >
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {t('reports.ritaseSummaryReport', 'Ritase Summary Report')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {t('reports.averageQuarryingRitase', 'Average Quarrying Ritase per Working Shift')}
                  </Typography>
                </Box>
              </Paper> */}
              <Box sx={{ mb: 1, width: '100%' }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'stretch' }}>
                  <Box sx={{ width: '33.33%' }}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'rgba(72, 93, 154, 0.15)',
                        bgcolor: '#ffffff',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(72, 93, 154, 0.12)',
                          transform: 'translateY(-2px)',
                          borderColor: '#485D9A'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: 'rgba(72, 93, 154, 0.08)',
                          color: '#485D9A',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <LocalShippingOutlinedIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                        >
                          {t('masterRoute.totalVehicles', 'Total Vehicles')}
                        </Typography>
                        <Typography variant="h4" fontWeight="800" color="#333" sx={{ mt: 0.5 }}>
                          {summaryTotals.totalVehicles}
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                  <Box sx={{ width: '33.33%' }}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'rgba(0, 167, 111, 0.15)',
                        bgcolor: '#ffffff',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0, 167, 111, 0.12)',
                          transform: 'translateY(-2px)',
                          borderColor: '#00A76F'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: 'rgba(0, 167, 111, 0.08)',
                          color: '#00A76F',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <AutorenewIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                        >
                          {t('reportModule.totalRitase', 'Total Ritase')}
                        </Typography>
                        <Typography variant="h4" fontWeight="800" color="#333" sx={{ mt: 0.5 }}>
                          {summaryTotals.totalRitase}
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                  <Box sx={{ width: '33.33%' }}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'rgba(255, 171, 0, 0.15)',
                        bgcolor: '#ffffff',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(255, 171, 0, 0.12)',
                          transform: 'translateY(-2px)',
                          borderColor: '#FFAB00'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: 'rgba(255, 171, 0, 0.08)',
                          color: '#FFAB00',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}
                        >
                          {t('reportModule.averagePerShift', 'Average per Shift')}
                        </Typography>
                        <Typography variant="h4" fontWeight="800" color="#333" sx={{ mt: 0.5 }}>
                          {summaryTotals.averagePerShift}
                        </Typography>
                      </Box>
                    </Card>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {loading && loadingProgress > 0 && reportType !== 'ritase_details' && (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, mb: 2 }}>
              <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2, textAlign: 'center' }}>
                <LinearProgress
                  variant="determinate"
                  value={loadingProgress}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    mb: 1.5,
                    bgcolor: 'rgba(72, 93, 154, 0.2)',
                    '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                  }}
                />
                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                  {loadingProgress}% - {loadingText}
                </Typography>
              </Box>
            </Box>
          )}

          {reportType === 'ritase_summary' ? (
            <RitaseSummaryTable
              title={activeConfig.title}
              columns={activeConfig.columns}
              rows={filteredRows}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              tableSearchQuery={tableSearchQuery}
              setTableSearchQuery={setTableSearchQuery}
            />
          ) : (
            <DataTable
              title={activeConfig.title}
              columns={activeConfig.columns}
              rows={filteredRows}
              loading={loading}
              pagination
              paginationMode="client"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              searchQuery={tableSearchQuery}
              onSearchChange={(e) => setTableSearchQuery(e.target.value)}
              searchLabel={t('reportModule.searchInTable', 'Search in table...')}
              hideAddButton={true}
              headerHeight={90}
              rowHeight={90}
            />
          )}
        </>
      )}
    </LocalizationProvider>
  );
}

export default function DataReportsPage() {
  return (
    <>
      <DataReportsPageContent />
    </>
  );
}
